import React, {
  useState,
  Fragment,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import ReactDOM from "react-dom";
import PageHead from "../../components/pageHead";
import Button from "../../components/button";
import IconButton from "../../components/iconButton";
import AddIcon from "../../icons/addIcon";
import CloseIcon from "../../icons/closeIcon";
import PrintIcon from "../../icons/printIcon";
import PDFDownloadIcon from "../../icons/pdfDownloadIcon";
import SnapshotCard from "../../components/snapshotCard";
import AddServiceModal from "../../components/addServiceModal";
import AddTrackDayModal from "../../components/addTrackDayModal";
import Pagination from "../../components/pagination";
import CardSkeleton from "../../components/cardSkeleton";
import NoData from "../../components/noData";
import useMediaQuery from "../../hooks/useMediaQuery";
// import { checkScrollDirection } from "../../utils/menuNav";
import MobileNav from "../../components/mobileNav";
import TutorialSnapshotImage from "../../assets/images/tutorialSnapshot.png";
import QuickStartGuide from "../../assets/files/DBQuickStartGuide.pdf";
import PrintPDF from "./printPDF";
import styles from "./style.module.css";
import { useAxiosPrivate } from "../../hooks/useAxiosPrivate";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

// Add PDF.js worker configuration
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`;

const Snapshot = () => {
  const targetRef = useRef();
  const axiosPrivate = useAxiosPrivate();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [openServiceDialog, setOpenServiceDialog] = useState(false);
  const [openTrackDayDialog, setOpenTrackDayDialog] = useState(false);
  const [isScrolling] = useState(false);
  const [snapshotData, setSnapshotData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPage, setSelectedPage] = useState(1);
  const [limitData] = useState(10);
  const [apiParams] = useState({ limit: limitData });
  const [totalData, setTotalData] = useState(0);
  const [allSnapshot, setAllSnapshot] = useState([]);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [pdfData, setPdfData] = useState(null);

  const handlePaginate = (value) => {
    let prm = apiParams;
    prm.page = value;
    setSelectedPage(value);
    setLoading(true);
    fetchSnapshot(prm);
  };

  const handleOpenServiceDialog = () => {
    setOpenServiceDialog(true);
  };

  const handleCloseServiceDialog = (changePage = false) => {
    setOpenServiceDialog(false);
    if (changePage) {
      fetchSnapshot(apiParams);
    }
  };

  const handleAddServiceTrackDay = (type) => {
    if (type === "add") {
      fetchSnapshot(apiParams);
    } else if (type === "update") {
      fetchSnapshot(apiParams);
    }
  };

  const handleOpenTrackDayDialog = () => {
    setOpenTrackDayDialog(true);
  };

  const handleCloseTrackDayDialog = (changePage = false) => {
    setOpenTrackDayDialog(false);
    if (changePage) {
      fetchSnapshot(apiParams);
    }
  };

  const handleScroll = (e) => {
    // const scrollDirection = checkScrollDirection(e);
    // setIsScrolling(scrollDirection);
  };

  const fetchSnapshot = useCallback(async (params, all = false) => {
    setLoading(true);
    try {
      let urlParams = "";
      if (params) {
        Object.keys(params).forEach(function (key, index) {
          urlParams += (index === 0 ? "?" : "&") + key + "=" + params[key];
        });
      }
      let url = `snapshot${urlParams}`;
      let options = {
        method: "GET",
        url,
      };
      await axiosPrivate(options)
        .then((response) => {
          if (response.data.status === 1) {
            if (all) {
              setAllSnapshot(response.data.snapshot);
            } else {
              setSnapshotData(response.data.snapshot);
              setTotalData(response.data.total);
            }
            setLoading(false);
          } else {
            setSnapshotData([]);
            setTotalData(0);
            setLoading(false);
          }
        })
        .catch((err) => {
          if (err.response) {
            setLoading(false);
            // console.error('err.res', err.response.data);
          }
        });
    } catch (error) {
      setLoading(false);
      // console.error('error', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generatePDF = async () => {
    setDownloadLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));
      const options = {
        overrides: {
          pdf: {},
          canvas: {},
        },
        page: {
          format: "a4",
          orientation: "portrait",
          margin: 10,
        },
        resolution: 4,
        canvas: {
          mimeType: "image/jpeg",
          qualityRatio: 1,
        },
      };

      const doc = new jsPDF({
        format: options.page.format,
        orientation: options.page.orientation,
      });

      const canvas = await html2canvas(targetRef.current, {
        scale: options.resolution,
        width: 1440,
        height: targetRef.current.scrollHeight + 100,
        windowWidth: 1440,
      });

      const imgData = canvas.toDataURL(
        options.canvas.mimeType,
        options.canvas.qualityRatio
      );

      const pdfWidth = doc.internal.pageSize.getWidth();
      const pdfHeight = doc.internal.pageSize.getHeight();

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(
        (pdfWidth - options.page.margin * 2) / imgWidth,
        (pdfHeight - options.page.margin * 2) / imgHeight
      );

      const scaledWidth = imgWidth * ratio;
      const scaledHeight = imgHeight * ratio;

      const x =
        options.page.margin +
        (pdfWidth - options.page.margin * 2 - scaledWidth) / 2;
      doc.addImage(imgData, "PNG", x, 10, scaledWidth, scaledHeight);
      doc.setDisplayMode("fullwidth");
      doc.setProperties({
        title: "DashBook Snapshot Report",
      });

      // Get the PDF as a blob instead of bloburl
      const pdfBlob = doc.output("blob");
      setPdfData(pdfBlob);
      setDownloadLoading(false);
    } catch (error) {
      console.error("Error generating PDF:", error);
      setDownloadLoading(false);
    }
  };

  const handlePrint = () => {
    if (pdfData) {
      const url = URL.createObjectURL(pdfData);
      document.querySelectorAll(".print_iframe_hide").forEach((item) => {
        item.remove();
      });
      const iframe = document.createElement("iframe");
      iframe.classList.add("print_iframe_hide");
      iframe.style.display = "none";
      iframe.style.border = "none";
      document.body.appendChild(iframe);

      // Add print styles
      const style = document.createElement("style");
      style.textContent = `
        @media print {
          body > *:not(.print_iframe_hide) {
            display: none !important;
          }
          .print_iframe_hide {
            position: static !important;
            width: 100% !important;
            height: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
            display: block !important;
          }
          @page {
            margin: 0;
            size: auto;
          }
        }
      `;
      document.head.appendChild(style);

      iframe.onload = () => {
        try {
          iframe.contentWindow.print();
        } catch (error) {
          console.error("Print failed:", error);
        }
      };

      iframe.src = url;
    }
  };

  const handleDownload = () => {
    if (pdfData) {
      const url = URL.createObjectURL(pdfData);
      const link = document.createElement("a");
      link.href = url;
      link.download = "DashBook_Snapshot_Report.pdf";
      link.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleClosePDF = () => {
    setPdfData(null);
    let iframe = document.querySelectorAll(".print_iframe_hide");
    if (iframe) {
      iframe.forEach((item) => {
        item.remove();
      });
    }
  };

  useEffect(() => {
    fetchSnapshot(apiParams, false);
    fetchSnapshot({ all: true }, true);
  }, [fetchSnapshot, apiParams]);

  return (
    <Fragment>
      <PrintPDF
        reference={targetRef}
        data={allSnapshot}
        downloadLoading={downloadLoading}
      />
      {pdfData &&
        ReactDOM.createPortal(
          <div className={styles.pdf_viewer}>
            <div className={styles.pdf_viewer_wrapper}>
              <div className={styles.pdf_actions}>
                <button
                  className={styles.pdf_action_btn}
                  type="button"
                  onClick={handlePrint}
                  title="Print PDF"
                >
                  <PrintIcon />
                </button>
                <button
                  className={styles.pdf_action_btn}
                  type="button"
                  onClick={handleDownload}
                  title="Download PDF"
                >
                  <PDFDownloadIcon />
                </button>
                <div
                  className={styles.pdf_action_btn}
                  type="button"
                  onClick={handleClosePDF}
                >
                  <CloseIcon />
                </div>
              </div>
              <Document
                file={pdfData}
                loading={
                  <div className={styles.pdf_loading}>
                    <div className={styles.pdf_loading_text}>
                      Loading PDF...
                    </div>
                  </div>
                }
                error={
                  <div className={styles.pdf_error}>Failed to load PDF.</div>
                }
                className={styles.pdf_document}
                options={{
                  cMapUrl:
                    "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/cmaps/",
                  cMapPacked: true,
                  standardFontDataUrl:
                    "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/standard_fonts/",
                }}
              >
                <Page
                  pageNumber={1}
                  width={Math.min(window.innerWidth * 0.8, 800)}
                  className={styles.pdf_page}
                />
              </Document>
            </div>
          </div>,
          document.body
        )}
      <PageHead
        title="Snapshot"
        logo={isMobile}
        tutorialContent={
          <Fragment>
            <p>
              The <strong>Snapshot Page</strong> is your main dashboard in
              DashBook. Once you've set up consumables and logged track days and
              services, you'll find a{" "}
              <strong>summary of everything here</strong>. If you haven’t
              already done so,{" "}
              <a
                href={QuickStartGuide}
                download="DashBook Quick-Start Guide.pdf"
              >
                download our Quick-Start Guide
              </a>{" "}
              for a step-by-step guide to setting up your DashBook account.
            </p>
            <p>
              If you’re missing some data initially, don’t worry—the page will
              become more accurate and informative as you continue to enter more
              information.{" "}
            </p>
            <img src={TutorialSnapshotImage} alt={"Snapshot Page"} />
            <p>
              Pay close attention to the <strong>Track Days Left</strong> field
              on the right—it shows how much life remains in each item. DashBook
              will alert you as service intervals approach:
            </p>
            <ol>
              <li>
                <strong>Yellow:</strong> 3 days remaining
              </li>
              <li>
                <strong>Red:</strong> 1 day remaining
              </li>
              <li>
                You can adjust these alert settings on your{" "}
                <strong>Profile Page</strong> (upper right corner of the
                screen).
              </li>
            </ol>
            <p>From the top of the Snapshot Page, you can quickly:</p>
            <ol>
              <li>
                <strong>Add New Service Entries</strong>
              </li>
              <li>
                <strong>Add New Track Days</strong>
              </li>
            </ol>
            <p>
              Additionally, you can{" "}
              <strong>download a printable version</strong> of your Snapshot to
              provide to your mechanic before tech inspections or service
              appointments.
            </p>
          </Fragment>
        }
      />
      <div className={styles.body}>
        <div className={styles.body_head}>
          <div className={styles.body_head_inner}>
            <div className={styles.add_btn}>
              <Fragment>
                <Button
                  title={isMobile ? "Service" : "Add New Service"}
                  buttonType="outlined"
                  buttonColor="primary"
                  startIcon={<AddIcon />}
                  onClick={handleOpenServiceDialog}
                />
                <Button
                  title={isMobile ? "Track Day" : "Add New Track Day"}
                  buttonType="outlined"
                  buttonColor="primary"
                  startIcon={<AddIcon />}
                  onClick={handleOpenTrackDayDialog}
                />
                {totalData > 0 && (
                  <IconButton
                    icon={<PrintIcon />}
                    onClick={generatePDF}
                    customClass={styles.print_btn}
                    isTooltip={true}
                    tooltipContent="Download Printable Version"
                    tooltipPosition={["top"]}
                    tooltipAlign="end"
                    tooltipTransform={{ top: -10, left: 0 }}
                  />
                )}
              </Fragment>
            </div>
          </div>
        </div>
        <div className={styles.body_view} onScroll={handleScroll}>
          {loading ? (
            <CardSkeleton />
          ) : (
            <Fragment>
              {snapshotData.map((snapshot, index) => (
                <SnapshotCard key={index} snapshot={snapshot} />
              ))}
            </Fragment>
          )}
          {totalData === 0 && !loading && <NoData />}
        </div>
        {totalData > 0 && (
          <div className={styles.body_foot}>
            {!isMobile && (
              <div className={styles.body_foot_num}>
                Total: {totalData} Records
              </div>
            )}
            <Pagination
              totalPages={Math.ceil(totalData / limitData)}
              limitData={limitData}
              selectedPage={selectedPage}
              handlePagination={handlePaginate}
            />
          </div>
        )}
      </div>
      <AddServiceModal
        open={openServiceDialog}
        onClose={handleCloseServiceDialog}
        onAddService={handleAddServiceTrackDay}
      />
      <AddTrackDayModal
        open={openTrackDayDialog}
        onClose={handleCloseTrackDayDialog}
        onAddTrackDay={handleAddServiceTrackDay}
      />
      {isMobile && <MobileNav defaultActive={true} isScrolling={isScrolling} />}
    </Fragment>
  );
};

export default Snapshot;
